import { useRecoilValue } from "recoil";

import { configState, priceState } from "../context/configState";
import { contractInfo, PoolInfo, TokenInfo } from "../types/configTypes";
import { sleep } from "../utils/common";
import { sleepTime } from "../utils/constants";

export const useConfig = () => {
  const configStore = useRecoilValue(configState);
  const priceStore = useRecoilValue(priceState);

  /*
   * Returns the pool data for given poolName from json config
   */
  const getPool = (poolName: string): PoolInfo => {
    if (!(poolName in configStore.poolsMap)) {
      // raise error
      throw new Error(`Pool ${poolName} does not exist`);
    }
    return configStore.poolsMap[poolName];
  };

  /*
   * Returns the list of pools from json config
   */
  const getPoolsList = (): PoolInfo[] => {
    return Object.values(configStore.poolsMap);
  };

  /*
   * Returns the first token data for given poolName from json config
   */
  const getPoolsFirstAsset = (poolName: string): TokenInfo => {
    if (!(poolName in configStore.poolsMap)) {
      // raise error
      throw new Error(`Pool ${poolName} does not exist`);
    }
    return getAsset(configStore.poolsMap[poolName].asset_infos[0]);
  };

  /*
   * Returns the second token data for given poolName from json config
   */
  const getPoolsSecondAsset = (poolName: string): TokenInfo => {
    if (!(poolName in configStore.poolsMap)) {
      // raise error
      throw new Error(`Pool ${poolName} does not exist`);
    }
    return getAsset(configStore.poolsMap[poolName].asset_infos[1]);
  };

  /*
   * Returns the LP token data for given poolName from json config
   */
  const getPoolsLPAsset = (poolName: string): { lpAddress: string, lpCodeHash: string } => {
    if (!(poolName in configStore.poolsMap)) {
      // raise error
      throw new Error(`Pool ${poolName} does not exist`);
    }
    return {
      lpAddress: configStore.poolsMap[poolName].liquidity_token,
      lpCodeHash: configStore.poolsMap[poolName].liquidity_token_hash,
    };
  };

  /*
   * Returns the assets token data for given tokenName from json config
   */
  const getAsset = (tokenName: string): TokenInfo => {
    if (!(tokenName in configStore.assetsMap)) {
      // raise error
      throw new Error(`Asset ${tokenName} does not exist`);
    }
    return configStore.assetsMap[tokenName] as TokenInfo;
  };

  /*
   * Returns the flag config from json config
   */
  const getFlags = () => {
    return [];
  };

  /*
   * Returns the contract data for given contractName from json config
   */
  const getOtherContract = (contractName: string): contractInfo => {
    if (!(contractName in configStore.otherContractsMap)) {
      // raise error
      throw new Error(
        `Contract ${contractName} does not exist in contracts list`
      );
    }
    return configStore.otherContractsMap[contractName];
  };

  /*
   * Returns the token's price for given tokenName from coingecko
   */
  const getAssetPrice = async (tokenName: string): Promise<string> => {
    while (Object.keys(priceStore.priceMap).length === 0) {
      await sleep(sleepTime);
    }
    const geckoId = getAsset(tokenName).coingecko_id;
    if (!(geckoId in priceStore.priceMap)) {
      // raise error
      throw new Error(
        `Asset ${tokenName} does not have a valid coingecko Id, or prices haven't been fetched`
      );
    }

    return priceStore.priceMap[geckoId].usd as string;
  };

  return {
    getPool,
    getPoolsList,
    getPoolsFirstAsset,
    getPoolsSecondAsset,
    getPoolsLPAsset,
    getAsset,
    getFlags,
    getOtherContract,
    getAssetPrice,
  };
};
